import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import { BlockMath, InlineMath } from "react-katex";
import "katex/dist/katex.min.css";

function isEscaped(text, idx) {
  let backslashes = 0;
  for (let i = idx - 1; i >= 0 && text[i] === "\\"; i -= 1) backslashes += 1;
  return backslashes % 2 === 1;
}

function findClosing(text, startIdx, delimiter) {
  const size = delimiter.length;
  for (let i = startIdx; i <= text.length - size; i += 1) {
    if (text.slice(i, i + size) === delimiter && !isEscaped(text, i)) return i;
  }
  return -1;
}

function normalizeForLatex(rawText) {
  const value = String(rawText || "");
  const hasMathHints = /(\$|\\\(|\\\[|\\\\\(|\\\\\[)/.test(value);
  if (!hasMathHints) return value;
  // GPT JSON sometimes double-escapes backslashes (e.g., "\\(" or "\\int").
  return value.replace(/\\\\/g, "\\");
}

function parseSegments(text) {
  const value = normalizeForLatex(text);
  const segments = [];
  let cursor = 0;
  let plain = "";

  const pushPlain = () => {
    if (!plain) return;
    segments.push({ type: "text", value: plain });
    plain = "";
  };

  while (cursor < value.length) {
    if (!isEscaped(value, cursor) && value.slice(cursor, cursor + 2) === "\\[") {
      const close = findClosing(value, cursor + 2, "\\]");
      if (close !== -1) {
        pushPlain();
        segments.push({ type: "display", value: value.slice(cursor + 2, close).trim() });
        cursor = close + 2;
        continue;
      }
    }

    if (!isEscaped(value, cursor) && value.slice(cursor, cursor + 2) === "\\(") {
      const close = findClosing(value, cursor + 2, "\\)");
      if (close !== -1) {
        pushPlain();
        segments.push({ type: "inline", value: value.slice(cursor + 2, close).trim() });
        cursor = close + 2;
        continue;
      }
    }

    if (!isEscaped(value, cursor) && value.slice(cursor, cursor + 2) === "$$") {
      const close = findClosing(value, cursor + 2, "$$");
      if (close !== -1) {
        pushPlain();
        segments.push({ type: "display", value: value.slice(cursor + 2, close).trim() });
        cursor = close + 2;
        continue;
      }
    }

    if (!isEscaped(value, cursor) && value[cursor] === "$") {
      const close = findClosing(value, cursor + 1, "$");
      if (close !== -1) {
        pushPlain();
        segments.push({ type: "inline", value: value.slice(cursor + 1, close).trim() });
        cursor = close + 1;
        continue;
      }
    }

    plain += value[cursor];
    cursor += 1;
  }

  pushPlain();
  return segments;
}

function renderError(err) {
  return <span>{err?.message || "Invalid LaTeX"}</span>;
}

export default function LatexText({ text = "", component = "span", sx = {} }) {
  const segments = useMemo(() => parseSegments(text), [text]);

  return (
    <Box component={component} sx={{ whiteSpace: "pre-wrap", ...sx }}>
      {segments.map((segment, idx) => {
        if (segment.type === "display") {
          return (
            <Box key={`${segment.type}-${idx}`} sx={{ my: 1 }}>
              <BlockMath math={segment.value} errorColor="#d32f2f" renderError={renderError} />
            </Box>
          );
        }
        if (segment.type === "inline") {
          return (
            <InlineMath
              key={`${segment.type}-${idx}`}
              math={segment.value}
              errorColor="#d32f2f"
              renderError={renderError}
            />
          );
        }
        return <span key={`${segment.type}-${idx}`}>{segment.value}</span>;
      })}
    </Box>
  );
}
